/**
 * SES Lockdown Initialization
 *
 * Freezes all JavaScript intrinsics to prevent prototype pollution.
 * Saves and restores Office.js-specific Function properties that
 * SES lockdown would destroy.
 *
 * IMPORTANT: SES is loaded lazily via dynamic import() to avoid
 * interfering with Office.js initialization at app startup.
 *
 * Based on: hewliyang/open-excel lockdown.ts
 */

/* global globalThis */

const PRESERVE_FUNCTION_PROPS = new Set(['length', 'name', 'prototype']);
const OFFICE_FUNCTION_STUBS = new Set([
  '_validateParams',
  '_validateParameterCount',
  '_validateParameter',
  '_validateParameterType',
]);

function saveFunctionProperties() {
  const saved = new Map();
  for (const key of Object.getOwnPropertyNames(Function)) {
    if (!PRESERVE_FUNCTION_PROPS.has(key)) {
      saved.set(key, Function[key]);
    }
  }
  return saved;
}

function restoreFunctionProperties(saved) {
  if (saved.size === 0) return;

  // Replace Office.js validation stubs with no-ops
  const noop = () => null;
  for (const key of OFFICE_FUNCTION_STUBS) {
    if (saved.has(key)) saved.set(key, noop);
  }

  const fn = globalThis.Function;

  // Fast path: if Function is still extensible, define properties directly
  if (Object.isExtensible(fn)) {
    try {
      for (const [key, value] of saved) {
        Object.defineProperty(fn, key, {
          value,
          writable: false,
          enumerable: false,
          configurable: false,
        });
      }
      return;
    } catch {
      // Fall through to proxy path
    }
  }

  // Slow path: Function is frozen by lockdown, wrap it in a Proxy
  const desc = Object.getOwnPropertyDescriptor(globalThis, 'Function');
  if (!desc || (!desc.writable && !desc.configurable)) {
    console.warn(
      '[lockdown] Cannot restore Function properties — Function is neither writable nor configurable'
    );
    return;
  }

  const proxy = new Proxy(fn, {
    get(target, prop, receiver) {
      return saved.has(prop)
        ? saved.get(prop)
        : Reflect.get(target, prop, receiver);
    },
    has(target, prop) {
      return saved.has(prop) ? true : Reflect.has(target, prop);
    },
    getOwnPropertyDescriptor(target, prop) {
      if (saved.has(prop)) {
        return {
          value: saved.get(prop),
          writable: false,
          enumerable: false,
          configurable: true,
        };
      }
      return Reflect.getOwnPropertyDescriptor(target, prop);
    },
  });

  Object.defineProperty(globalThis, 'Function', {
    value: proxy,
    writable: desc.writable ?? false,
    configurable: desc.configurable ?? false,
  });
}

let locked = false;
let sesLoadPromise = null;

/**
 * Lazily load the SES library via dynamic import.
 * Only called when sandboxed execution is actually needed.
 */
function loadSES() {
  if (!sesLoadPromise) {
    sesLoadPromise = import('ses').then(
      () => true,
      (err) => {
        console.warn('[lockdown] Failed to load SES:', err);
        sesLoadPromise = null; // Allow retry
        throw err;
      }
    );
  }
  return sesLoadPromise;
}

/**
 * Ensure SES lockdown has been performed exactly once.
 * Safe to call multiple times — subsequent calls are no-ops.
 * Async because SES is loaded lazily.
 */
export async function ensureLockdown() {
  if (locked) return;
  try {
    await loadSES();
    const savedFnProps = saveFunctionProperties();
    // lockdown is added to globalThis by 'ses' module
    globalThis.lockdown({
      errorTaming: 'unsafe',
      consoleTaming: 'unsafe',
      overrideTaming: 'severe',
      stackFiltering: 'verbose',
    });
    locked = true;
    restoreFunctionProperties(savedFnProps);
  } catch (e) {
    if (
      e instanceof TypeError &&
      String(e).includes('SES_ALREADY_LOCKED_DOWN')
    ) {
      locked = true;
    } else {
      throw e;
    }
  }
}
